import { formatDateTimeBR } from "../../../../../../helpers/format";

export interface ISangriaPdfPayload {
  sangria: {
    id_operacao_patio_guiche_movimentacao_sangria: number;
    id_operacao_patio_guiche_movimentacao: number;
    valor_sangria: number;
    justificativa: string;
    data_historico: string;
    usuario?: string;
  };
  id_operacao_patio_guiche?: number | string | null;
}

const formatCurrency = (value: number | string | null | undefined) => {
  const numericValue = Number(value ?? 0);

  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(Number.isNaN(numericValue) ? 0 : numericValue);
};

const formatDate = (value?: string | null) => {
  if (!value) {
    return "---";
  }

  try {
    return formatDateTimeBR(value);
  } catch {
    return value;
  }
};

const escapeHtml = (value: string) => value
  .replaceAll("&", "&amp;")
  .replaceAll("<", "&lt;")
  .replaceAll(">", "&gt;")
  .replaceAll('"', "&quot;")
  .replaceAll("'", "&#39;");

const createResumoRows = (sangria: ISangriaPdfPayload["sangria"]) => ([
  ["Valor da Sangria", formatCurrency(sangria.valor_sangria)],
  ["Justificativa", sangria.justificativa?.trim() || "---"],
]);

const createPrintHtml = ({ sangria, id_operacao_patio_guiche }: ISangriaPdfPayload) => {
  const resumoRows = createResumoRows(sangria)
    .map(([label, value]) => `
      <tr>
        <td>${escapeHtml(label)}</td>
        <td>${escapeHtml(value)}</td>
      </tr>
    `)
    .join("");

  return `<!DOCTYPE html>
    <html lang="pt-BR">
      <head>
        <meta charset="UTF-8" />
        <title>Sangria de Caixa</title>
        <style>
          :root {
            color-scheme: light;
          }

          * {
            box-sizing: border-box;
          }

          body {
            margin: 0;
            padding: 24px;
            font-family: Arial, Helvetica, sans-serif;
            color: #1f2937;
            background: #ffffff;
          }

          .page {
            max-width: 1024px;
            margin: 0 auto;
          }

          .header {
            display: flex;
            justify-content: space-between;
            gap: 24px;
            margin-bottom: 24px;
          }

          .title {
            font-size: 28px;
            font-weight: 700;
            color: #062d4e;
            margin-bottom: 8px;
          }

          .subtitle {
            font-size: 14px;
            color: #6b7280;
            line-height: 1.5;
          }

          .meta {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 12px;
            margin-bottom: 24px;
          }

          .meta-card {
            border: 1px solid #d1d5db;
            border-radius: 12px;
            padding: 14px 16px;
            background: #f9fafb;
          }

          .meta-label {
            font-size: 12px;
            font-weight: 700;
            text-transform: uppercase;
            color: #6b7280;
            margin-bottom: 6px;
          }

          .meta-value {
            font-size: 15px;
            font-weight: 600;
            color: #111827;
            word-break: break-word;
          }

          .section {
            margin-bottom: 28px;
          }

          .section-title {
            font-size: 18px;
            font-weight: 700;
            color: #0c4a85;
            margin-bottom: 12px;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            table-layout: fixed;
          }

          th,
          td {
            border: 1px solid #d1d5db;
            padding: 10px 12px;
            text-align: left;
            vertical-align: top;
            word-wrap: break-word;
          }

          th {
            background: #062d4e;
            color: #ffffff;
            font-size: 13px;
          }

          tbody tr:nth-child(even) {
            background: #f9fafb;
          }

          .empty-state {
            text-align: center;
            color: #6b7280;
            padding: 24px 12px;
          }

          .footer {
            margin-top: 24px;
            font-size: 12px;
            color: #6b7280;
          }

          @media print {
            body {
              padding: 12px;
            }

            .page {
              max-width: none;
            }
          }
        </style>
      </head>
      <body>
        <div class="page">
          <div class="header">
            <div>
              <div class="title">Sangria de Caixa</div>
              <div class="subtitle">Comprovante de retirada de valores do caixa.</div>
            </div>
          </div>

          <div class="meta">
            <div class="meta-card">
              <div class="meta-label">Sangria ID</div>
              <div class="meta-value">${escapeHtml(String(sangria.id_operacao_patio_guiche_movimentacao_sangria ?? "---"))}</div>
            </div>
            <div class="meta-card">
              <div class="meta-label">Movimentacao ID</div>
              <div class="meta-value">${escapeHtml(String(sangria.id_operacao_patio_guiche_movimentacao ?? "---"))}</div>
            </div>
            <div class="meta-card">
              <div class="meta-label">Guiche ID</div>
              <div class="meta-value">${escapeHtml(String(id_operacao_patio_guiche ?? "---"))}</div>
            </div>
            <div class="meta-card">
              <div class="meta-label">Usuario</div>
              <div class="meta-value">${escapeHtml(sangria.usuario || "---")}</div>
            </div>
            <div class="meta-card" style="grid-column: span 2;">
              <div class="meta-label">Data e Hora</div>
              <div class="meta-value">${escapeHtml(formatDate(sangria.data_historico))}</div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">Detalhamento da Sangria</div>
            <table>
              <thead>
                <tr>
                  <th>Campo</th>
                  <th>Valor</th>
                </tr>
              </thead>
              <tbody>
                ${resumoRows}
              </tbody>
            </table>
          </div>

          <div class="footer">Gerado em ${escapeHtml(formatDate(new Date().toISOString()))}</div>
        </div>
      </body>
    </html>`;
};

export const generateSangriaPdf = ({ sangria, id_operacao_patio_guiche }: ISangriaPdfPayload) => {
  const html = createPrintHtml({ sangria, id_operacao_patio_guiche });

  const iframe = globalThis.document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.position = "fixed";
  iframe.style.right = "100%";
  iframe.style.bottom = "100%";
  iframe.style.width = "210mm";
  iframe.style.height = "297mm";
  iframe.style.border = "0";
  iframe.style.opacity = "0.01";
  iframe.style.pointerEvents = "none";

  const cleanup = () => {
    iframe.remove();
  };

  iframe.onload = () => {
    const frameWindow = iframe.contentWindow;

    if (!frameWindow) {
      cleanup();
      throw new Error("Nao foi possivel carregar o documento para impressao.");
    }

    const onAfterPrint = () => {
      frameWindow.removeEventListener("afterprint", onAfterPrint);
      cleanup();
    };

    frameWindow.addEventListener("afterprint", onAfterPrint);
    globalThis.setTimeout(() => {
      frameWindow.focus();
      frameWindow.print();
    }, 50);

    // Fallback caso o evento afterprint nao dispare no navegador.
    globalThis.setTimeout(cleanup, 60000);
  };

  globalThis.document.body.appendChild(iframe);
  iframe.srcdoc = html;
};
